import React from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { Box, Typography } from '@mui/material';

const GREEN = '#1a4d2e';
const GREEN_LIGHT = '#2d7a4f';
const GREEN_PALE = '#eaf3de';

const CustomTooltip = ({ active, payload, label, unit = 'hrs' }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{
        background: 'white',
        border: '0.5px solid #e0e8e0',
        borderRadius: 2,
        p: 1.5,
        fontSize: 12,
      }}>
        <Typography sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }}>{label}</Typography>
        {payload.map((p) => (
          <Typography key={p.name} sx={{ fontSize: 12, color: p.color }}>
            {p.name}: {p.value} {unit}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

export function HoursBarChart({ data, dataKey = 'hours', xKey = 'date', label = 'Hours worked' }) {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: '#aaa', fontSize: 13 }}>
        No data available for this period
      </Box>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#888' }} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#888' }} tickLine={false} axisLine={false} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey={dataKey} name={label} fill={GREEN} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function HoursLineChart({ data, dataKey = 'hours', xKey = 'month', label = 'Hours' }) {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: '#aaa', fontSize: 13 }}>
        No data available for this period
      </Box>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#888' }} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#888' }} tickLine={false} axisLine={false} />
        <Tooltip content={<CustomTooltip />} />
        <Line
          type="monotone"
          dataKey={dataKey}
          name={label}
          stroke={GREEN}
          strokeWidth={2}
          dot={{ fill: GREEN, r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function MultiLineChart({ data, lines, xKey = 'month' }) {
  const colors = [GREEN, GREEN_LIGHT, '#854f0b', '#0c447c'];
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: '#aaa', fontSize: 13 }}>
        No data available for this period
      </Box>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#888' }} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#888' }} tickLine={false} axisLine={false} />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {lines.map((line, i) => (
          <Line
            key={line.key}
            type="monotone"
            dataKey={line.key}
            name={line.label}
            stroke={colors[i % colors.length]}
            strokeWidth={2}
            dot={{ fill: colors[i % colors.length], r: 3 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}